import { Injectable, inject } from '@angular/core'
import { DateAdapter } from '@angular/material/core'
import { HttpService } from '@core/request/http.service'
import { type numeric } from '@core/type/numeric'
import { type AppOption } from '@core/type/option'
import { IS_A_STRING_AND_NOT_EMPTY, IS_NUMERIC, IS_SET } from '@core/util/check.util'
import { OPTIONS_TO_KEY_VALUES, TO_NUMBER } from '@core/util/convert.util'
import { ENV_CDMX, ENV_MONTREAL } from '@core/util/env.util'
import { GET_LOCALE, SET_LOCALE } from '@core/util/space.util'
import { TO_STRING } from '@core/util/string.util'
import { FORMAT, GET_TZ, SET_TZ } from '@core/util/time.util'
import { environment } from '@env/environment'
import { DateTime } from 'luxon'
import { BehaviorSubject } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class LocaleService {
  HttpService = inject(HttpService)
  DateAdapter = inject(DateAdapter)

  localeBS = new BehaviorSubject<string>(GET_LOCALE() ?? environment.locale ?? 'en-gb')

  tzBS = new BehaviorSubject<string>(GET_TZ() ?? environment.tz ?? 'Europe/Paris')

  geoOptions = [
    { value: 'ca', label: 'Canada' },
    { value: 'gb', label: 'United Kingdom' },
    { value: 'it', label: 'Italia' },
    { value: 'mx', label: 'México' },
    { value: 'fr', label: 'France' },
    { value: 'pt', label: 'Portugal' },
  ]

  geoName = OPTIONS_TO_KEY_VALUES(this.geoOptions)

  langOptionList: AppOption[] = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'it', label: 'Italiano' },
    { value: 'fr', label: 'Français' },
    { value: 'pt', label: 'Português' },
  ]

  langName = OPTIONS_TO_KEY_VALUES(this.langOptionList)

  supportedLocaleList = ['en-gb', 'es-mx', 'fr-fr', 'it-it', 'pt-pt']

  localeOptions: Array<{ value: string, label: string }> = this.supportedLocaleList.map(locale => {
    const langName = this.getLangName({ locale })
    const bLangName = IS_A_STRING_AND_NOT_EMPTY(langName)
    const geoName = this.getGeoName({ locale })
    const bGeoName = IS_A_STRING_AND_NOT_EMPTY(geoName)
    return {
      value: locale,
      label: (bLangName || bGeoName) ? `${langName}${bGeoName ? ` (${geoName})` : ''}` : ''
    }
  })

  localeName: Record<string, string> = OPTIONS_TO_KEY_VALUES(this.localeOptions)

  langMainLocale: Record<string, string> = {
    en: 'en-gb', // en-us (ENV_USA)
    es: ENV_CDMX ? 'es-mx' : 'es-es',
    it: 'it-it',
    fr: ENV_MONTREAL ? 'fr-ca' : 'fr-fr',
    pt: 'pt-pt',
  }

  tzOptions = [
    { value: 'UTC', label: 'UTC' },
    { value: 'America/Mexico_City', label: 'Puebla' },
    { value: 'America/Montreal', label: 'Montréal' },
    { value: 'Europe/Paris', label: 'Paris' }
  ]

  tzName = OPTIONS_TO_KEY_VALUES(this.tzOptions)

  authorizationOptions = [
    { value: 1, label: 'CV' },
    { value: 2, label: 'CL' },
    { value: 4, label: 'CP' },
    { value: 8, label: 'CPP' }
  ]

  valueToAuth = OPTIONS_TO_KEY_VALUES(this.authorizationOptions)

  static defaultLocale = 'en-gb'

  getSupportedTzList (): string[] {
    const res = ENV_MONTREAL ? ['UTC', 'America/Montreal', 'Europe/Paris'] : ['UTC', 'America/Mexico_City', 'Europe/Paris']
    return res
  }

  getSupportedTzOptions (): AppOption[] {
    const supportedTzList = this.getSupportedTzList()
    return this.tzOptions.filter(o => supportedTzList.includes(o.value))
  }

  async setLocale (locale: string): Promise<void> {
    SET_LOCALE(locale)

    this.DateAdapter.setLocale(locale)

    const usLocaleText = locale
    const usLocaleNumber = locale
    const usLocaleTime = locale
    const usLang = this.getLang({ locale })
    const usGeo = this.getGeo({ locale })

    await this.HttpService.put({ path: '/us_self', body: { us: { usLocaleText, usLocaleNumber, usLocaleTime, usLang, usGeo } } })

    this.localeBS.next(locale)
  }

  getLocale (_?: { locale?: string, lang?: string }): string {
    let res = TO_STRING(_?.locale)

    if (!IS_A_STRING_AND_NOT_EMPTY(res)) {
      const _lang = TO_STRING(_?.lang)
      res = TO_STRING(IS_A_STRING_AND_NOT_EMPTY(_lang) ? this.langMainLocale[_lang] : this.localeBS.value)
    }

    return IS_A_STRING_AND_NOT_EMPTY(res) ? res : LocaleService.defaultLocale
  }

  getLang (_?: { locale?: any, lang?: any }): string {
    return TO_STRING(_?.lang ?? this.getLocale(_).split('-')[0])
  }

  getGeo (_?: { locale?: any, lang?: any }): string {
    return TO_STRING((_?.locale ?? this.getLocale(_)).split('-')[1])
  }

  async setTz (tz: string): Promise<void> {
    SET_TZ(tz)

    await this.HttpService.put({ path: '/us_self', body: { us: { usTimezone: tz } } })

    this.tzBS.next(tz)
  }

  getTz (): string {
    return this.tzBS.value
  }

  localizeNumber (_: { value: numeric, options?: Intl.NumberFormatOptions }): string {
    const { value, options } = _
    return IS_NUMERIC(value) ? TO_NUMBER(value).toLocaleString(this.getLocale(), options) : ''
  }

  localizeM (_: { input?: any, epoch?: any, inTz?: any, inFormat?: any, outTz?: any, outFormat?: any }): string {
    const { epoch, inTz, inFormat, outTz, outFormat } = _

    const input = _.input ?? (1000 * TO_NUMBER(epoch))

    let m = (
      IS_SET(inFormat)
        ? DateTime.fromFormat(TO_STRING(input), TO_STRING(inFormat), IS_SET(inTz) ? { zone: inTz } : undefined)
        : (IS_NUMERIC(input) ? DateTime.fromMillis(TO_NUMBER(input)) : DateTime.fromISO(TO_STRING(input)))
    )

    if (IS_SET(outTz)) {
      m = m.setZone(TO_STRING(outTz))
    }

    return m.isValid ? (IS_SET(outFormat) ? m.toFormat(TO_STRING(outFormat)) : TO_STRING(m.toISO())) : ''
  }

  localizeYmd (_: { input?: any, epoch?: any, outTz?: any }): string {
    return this.localizeM({ ..._, outFormat: FORMAT.localYmd })
  }

  localizeHm (_: { input?: any, epoch?: any, outTz?: any }): string {
    return this.localizeM({ ..._, outFormat: FORMAT.localHm })
  }

  localizeHms (_: { input?: any, epoch?: any, outTz?: any }): string {
    return this.localizeM({ ..._, outFormat: FORMAT.localHms })
  }

  localizeYmdHm (_: { input?: any, epoch?: any, outTz?: any }): string {
    return this.localizeM({ ..._, outFormat: FORMAT.localYmdHm })
  }

  localizeYmdHms (_: { input?: any, epoch?: any, outTz?: any }): string {
    return this.localizeM({ ..._, outFormat: FORMAT.localYmdHms })
  }

  getLocaleName (_?: { locale?: any, lang?: any }): string {
    const requested = this.getLocale(_)
    return TO_STRING(this.localeName[(IS_A_STRING_AND_NOT_EMPTY(requested) ? requested : this.getLocale()) ?? '!!!'])
  }

  getLangName (_?: { locale?: any, lang?: any }): string {
    const requested = this.getLang(_)
    return TO_STRING(this.langName[(IS_A_STRING_AND_NOT_EMPTY(requested) ? requested : this.getLang()) ?? '!!!'])
  }

  getGeoName (_?: { locale?: any, lang?: any }): string {
    const requested = this.getGeo(_)
    return TO_STRING(this.geoName[(IS_A_STRING_AND_NOT_EMPTY(requested) ? requested : this.getGeo()) ?? '!!!'])
  }

  toLC (s: string): string {
    return s.toLocaleLowerCase(this.getLocale())
  }

  toUC (s: string): string {
    return s.toLocaleUpperCase(this.getLocale())
  }

  toUCF (s: string): string {
    const locale = this.getLocale()
    const UCF = TO_STRING(s[0]).toLocaleUpperCase(locale)
    const lower = s.substring(1).toLocaleLowerCase(locale)
    return `${UCF}${lower}`
  }

  displayAuthFromValue (v: any): string {
    const res: string[] = []

    if (IS_NUMERIC(v)) {
      const n = parseInt(TO_STRING(v))

      if ((n & 1) > 0) {
        res.push(TO_STRING(this.valueToAuth[1]))
      }
      if ((n & 2) > 0) {
        res.push(TO_STRING(this.valueToAuth[2]))
      }
      if ((n & 4) > 0) {
        res.push(TO_STRING(this.valueToAuth[4]))
      }
      if ((n & 8) > 0) {
        res.push(TO_STRING(this.valueToAuth[8]))
      }
    }

    return res.join(' / ')
  }
}
